/**
 * 1.4.8 — Bulk Import de contactos por CSV. Flujo: descargar plantilla → subir CSV
 * → mapeo de columnas (auto-match por encabezado) → previsualizar → importar.
 * El backend (POST /contacts/import) valida por fila + dedup por email y devuelve
 * un reporte (importados / duplicados / errores con motivo). Solo se muestra a
 * roles con `contacts:create` (gate de UI; el API además exige el permiso).
 */
import { useMemo, useState } from 'react';
import { api } from '../lib/api';

const btnPrimary = 'rounded-xl bg-[var(--brand-primary)] px-3 py-1.5 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

const FIELDS: Array<{ key: string; label: string; required?: boolean; syn: string[] }> = [
  { key: 'firstName', label: 'Nombre', required: true, syn: ['firstname', 'first name', 'nombre', 'first', 'nombres'] },
  { key: 'lastName', label: 'Apellido', syn: ['lastname', 'last name', 'apellido', 'last', 'apellidos'] },
  { key: 'email', label: 'Email', syn: ['email', 'correo', 'e-mail', 'mail', 'correo electronico'] },
  { key: 'phone', label: 'Teléfono', syn: ['phone', 'telefono', 'teléfono', 'tel', 'celular', 'movil', 'móvil', 'whatsapp'] },
  { key: 'company', label: 'Empresa', syn: ['company', 'empresa', 'compania', 'compañia', 'compañía'] },
  { key: 'jobTitle', label: 'Cargo', syn: ['jobtitle', 'job title', 'cargo', 'puesto', 'titulo', 'título'] },
  { key: 'source', label: 'Origen', syn: ['source', 'origen', 'fuente'] },
  { key: 'notes', label: 'Notas', syn: ['notes', 'notas', 'observaciones', 'nota', 'comentarios'] },
];

const TEMPLATE_HEADERS = FIELDS.map((f) => f.key).join(',');
const TEMPLATE_CSV =
  `${TEMPLATE_HEADERS}\n` +
  `Juan,Perez,juan@example.com,5512345678,Mi Empresa,Gerente,referido,Cliente frecuente\n`;

const norm = (s: string) => s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Parser CSV mínimo pero correcto: comillas, comas y comillas escapadas (""). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') inQuotes = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else if (ch !== '\r') field += ch;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'plantilla-contactos.csv';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: any }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-serif text-lg tracking-tight text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function BulkImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, number>>({}); // field.key -> column index (-1 = none)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [report, setReport] = useState<any | null>(null);

  const onFile = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null); setReport(null);
    const text = await file.text();
    const rows = parseCsv(text);
    if (rows.length < 2) { setErr('El CSV no tiene filas de datos (encabezado + al menos una fila).'); return; }
    const hdr = rows[0];
    setHeaders(hdr);
    setDataRows(rows.slice(1));
    // Auto-match por encabezado.
    const auto: Record<string, number> = {};
    for (const f of FIELDS) {
      const idx = hdr.findIndex((h) => f.syn.includes(norm(h)) || norm(h) === f.key.toLowerCase());
      auto[f.key] = idx;
    }
    setMapping(auto);
  };

  const builtRows = useMemo(() => {
    if (!dataRows.length) return [];
    return dataRows.map((cols) => {
      const obj: Record<string, string> = {};
      for (const f of FIELDS) {
        const idx = mapping[f.key];
        if (idx != null && idx >= 0) obj[f.key] = (cols[idx] ?? '').trim();
      }
      return obj;
    });
  }, [dataRows, mapping]);

  const firstNameMapped = (mapping.firstName ?? -1) >= 0;

  const doImport = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api('/contacts/import', { method: 'POST', body: { rows: builtRows } });
      setReport(r);
    } catch (e: any) {
      setErr(e?.body?.message || e?.message || 'No se pudo importar');
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Importar contactos (CSV)" onClose={onClose}>
      {!report ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <span className="text-sm text-slate-600">¿No tienes el archivo? Descarga la plantilla con los encabezados correctos.</span>
            <button className={btnGhost} onClick={downloadTemplate}>Descargar plantilla</button>
          </div>
          {/* V92 — leyenda de consentimiento (LFPDPPP): los importados quedan
              elegibles para comunicados; las bajas previas se respetan (la
              supresión vive por email y sobrevive re-imports). */}
          <p className="text-[11px] text-slate-400">
            Importa solo contactos que te dieron su correo al inscribirse: podrán
            recibir comunicados del negocio (con baja en cada correo, que se
            respeta aunque re-importes la lista).
          </p>

          <div>
            <p className="mb-1.5 text-xs font-medium text-slate-600">1. Sube tu CSV</p>
            <input type="file" accept=".csv,text/csv" onChange={onFile}
              className="w-full text-xs file:mr-3 file:rounded-lg file:border file:border-slate-200 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50" />
          </div>

          {headers.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-slate-600">2. Mapea las columnas ({dataRows.length} filas)</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <label key={f.key} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                    <span className="text-slate-700">{f.label}{f.required && <span className="text-rose-500"> *</span>}</span>
                    <select className="input !w-40 !py-1 text-xs"
                      value={mapping[f.key] ?? -1}
                      onChange={(e) => setMapping((m) => ({ ...m, [f.key]: Number(e.target.value) }))}>
                      <option value={-1}>— ninguna —</option>
                      {headers.map((h, i) => <option key={i} value={i}>{h || `(col ${i + 1})`}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              {!firstNameMapped && <p className="mt-2 text-[11px] text-rose-600">Mapea al menos la columna de Nombre.</p>}
            </div>
          )}

          {err && <p className="alert-over rounded-xl px-3 py-2 text-xs">{err}</p>}

          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button className={btnGhost} onClick={onClose}>Cancelar</button>
            <button className={btnPrimary} disabled={busy || !headers.length || !firstNameMapped} onClick={doImport}>
              {busy ? 'Importando…' : `Importar ${dataRows.length} fila${dataRows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl bg-emerald-50 px-4 py-3"><p className="font-serif text-2xl text-emerald-700">{report.imported}</p><p className="text-[11px] text-emerald-600">Importados</p></div>
            <div className="rounded-xl bg-slate-100 px-4 py-3"><p className="font-serif text-2xl text-slate-600">{report.skippedDuplicates}</p><p className="text-[11px] text-slate-500">Duplicados omitidos</p></div>
            <div className="rounded-xl bg-rose-50 px-4 py-3"><p className="font-serif text-2xl text-rose-700">{report.errors.length}</p><p className="text-[11px] text-rose-600">Con error</p></div>
          </div>
          {report.errors.length > 0 && (
            <div className="max-h-48 overflow-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500"><tr><th className="px-3 py-1.5">Fila</th><th className="px-3 py-1.5">Motivo</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {report.errors.map((e: any, i: number) => (
                    <tr key={i}><td className="px-3 py-1.5 tabular-nums text-slate-500">{e.row}</td><td className="px-3 py-1.5 text-slate-700">{e.reason}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end">
            <button className={btnPrimary} onClick={onDone}>Listo</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
